import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';
import * as authService from '../services/authService';
import * as agentService from '../services/agentService';
import * as notificationService from '../services/notificationService';
import { socketService } from '../services/socketService';
import { saveServerKeys } from '../services/settingsKeys';
import { apiFetch } from '../services/apiClient';
import { encryptString } from '../utils/crypto';

interface UserProfile {
  uid: string;
  username: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  balance: number;
  role: string;
  phone?: string;
  provider: 'custom' | 'google';
}

interface Agent {
  id: string;
  ownerUid: string;
  name: string;
  category: string;
  data: string;
  createdAt: string;
  status: 'active' | 'inactive';
}

interface AIConfig {
  provider: string;
  model: string;
  apiKey: string;
}

interface VisionConfig {
  provider: string;
  model: string;
  apiKey: string;
}

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
}

interface ToolOverride {
  enabled: boolean;
  securityLevel?: string;
}

export type OperationMode = 'chat' | 'assistant' | 'autonomous' | 'meeting';

function normalizeOperationMode(mode: unknown): OperationMode {
  if (mode === 'chat' || mode === 'assistant' || mode === 'autonomous' || mode === 'meeting') return mode;
  if (mode === 'music') return 'assistant';
  if (mode === 'desktop_control' || mode === 'terminal') return 'assistant';
  return 'assistant';
}

export interface OrgConnection {
  orgId: string;
  orgRole: string;
  orgName: string;
  connected: boolean;
}

export interface DomainSwitchResult {
  success: boolean;
  domain: 'personal' | 'work';
  message?: string;
  connection?: OrgConnection | null;
}

interface AppContextType {
  user: UserProfile | null;
  loading: boolean;
  agents: Agent[];
  aiConfig: AIConfig;
  visionConfig: VisionConfig;
  // Voice
  selectedVoiceId: string | undefined;
  setSelectedVoiceId: (id: string, provider?: string) => void;
  favoriteVoices: string[];
  toggleFavoriteVoice: (id: string) => void;
  autoSpeakReply: boolean;
  setAutoSpeakReply: (v: boolean) => void;
  // Notifications
  notifications: NotificationItem[];
  unreadCount: number;
  addNotification: (item: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => void;
  markAllNotificationsRead: () => void;
  clearNotifications: () => void;
  // Tools
  toolOverrides: Record<string, ToolOverride>;
  setToolOverride: (name: string, override: ToolOverride) => void;
  // Org
  orgConnection: OrgConnection | null;
  workDomain: 'personal' | 'work';
  switchDomain: (domain: 'personal' | 'work') => Promise<DomainSwitchResult>;
  // Operation mode
  operationMode: OperationMode;
  setOperationMode: (mode: OperationMode) => void;
  // Core
  login: () => Promise<void>;
  logout: () => Promise<void>;
  createAgent: (name: string, category: string, data: any) => Promise<any>;
  updateAgent: (id: string, updates: Partial<Agent>) => Promise<any>;
  deleteAgent: (id: string) => Promise<void>;
  updateBalance: (amount: number) => Promise<void>;
  refreshUser: () => Promise<void>;
  updateAIConfig: (config: Partial<AIConfig>) => void;
  updateVisionConfig: (config: Partial<VisionConfig>) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// ── P6: One-time localStorage key migration (lumi_* → xf_*) ──
function migrateLocalStorage(): void {
  try {
    if (localStorage.getItem('xf_storage_migrated')) return;

    const lumiKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('lumi_')) {
        lumiKeys.push(key);
      }
    }

    let migrated = 0;
    for (const oldKey of lumiKeys) {
      const newKey = oldKey.replace(/^lumi_/, 'xf_');
      if (!localStorage.getItem(newKey)) {
        const value = localStorage.getItem(oldKey);
        if (value !== null) {
          localStorage.setItem(newKey, value);
          migrated++;
          console.log(`[migrate] ${oldKey} → ${newKey} … OK`);
        }
      }
    }

    localStorage.setItem('xf_storage_migrated', '1');
    if (migrated > 0) {
      console.log(`[migrate] Migrated ${migrated} localStorage keys from xf_* to xf_*`);
    }
  } catch (err) {
    console.warn('[AppContext] 本地存储迁移失败，已忽略:', err);
    // Don't block startup if migration fails
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  // Run migration before any state initializers that read from localStorage
  migrateLocalStorage();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiConfig, setAiConfig] = useState<AIConfig>(() => {
    const saved = localStorage.getItem('xf_ai_config');
    return saved ? JSON.parse(saved) : { provider: 'deepseek', model: 'deepseek-chat', apiKey: '' };
  });
  const [visionConfig, setVisionConfig] = useState<VisionConfig>(() => {
    const saved = localStorage.getItem('xf_vision_config');
    return saved ? JSON.parse(saved) : { provider: 'openai', model: 'gpt-4o', apiKey: '' };
  });

  useEffect(() => {
    apiFetch('/api/preferences/vision')
      .then(r => r.ok ? r.json() : null)
      .then(pref => {
        if (!pref?.provider) return;
        setVisionConfig(prev => {
          const next = {
            ...prev,
            provider: pref.provider,
            model: pref.model || pref.models?.[pref.provider] || prev.model,
          };
          localStorage.setItem('xf_vision_config', JSON.stringify(next));
          if (pref.models) localStorage.setItem('xf_vision_models', JSON.stringify(pref.models));
          return next;
        });
      })
      .catch(() => {});
  }, []);
  // Voice state
  const [selectedVoiceId, setSelectedVoiceIdState] = useState<string | undefined>(() => {
    return localStorage.getItem('xf_selected_voice_id') || undefined;
  });
  const [favoriteVoices, setFavoriteVoices] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('xf_favorite_voices') || '[]'); } catch { return []; }
  });
  const [autoSpeakReply, setAutoSpeakReplyState] = useState<boolean>(() => {
    // 默认关闭：回复不再自动朗读，需用户在设置中主动开启
    return localStorage.getItem('xf_auto_speak_reply') === 'true';
  });

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Tool overrides state
  const [toolOverrides, setToolOverrides] = useState<Record<string, ToolOverride>>(() => {
    try { return JSON.parse(localStorage.getItem('xf_tool_overrides') || '{}'); } catch { return {}; }
  });

  // Org state
  const [orgConnection, setOrgConnection] = useState<OrgConnection | null>(() => {
    try { return JSON.parse(localStorage.getItem('xf_org_connection') || 'null'); } catch { return null; }
  });
  const [workDomain, setWorkDomain] = useState<'personal' | 'work'>(() => {
    try { return (localStorage.getItem('xf_work_domain') as 'personal' | 'work') || 'personal'; } catch { return 'personal'; }
  });

  const switchDomain = async (domain: 'personal' | 'work'): Promise<DomainSwitchResult> => {
    if (domain === 'personal') {
      try {
        const res = await apiFetch('/api/auth/switch-org', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgId: null }),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.token) {
            localStorage.setItem('xf_auth_token', encryptString(data.token));
            socketService.refreshAuth();
          }
          setWorkDomain('personal');
          localStorage.setItem('xf_work_domain', 'personal');
          window.dispatchEvent(new CustomEvent('xf:domain-changed', {
            detail: { domain: 'personal', orgId: null, orgRole: null },
          }));
          return { success: true, domain: 'personal', message: '已切换到个人域', connection: orgConnection };
        }
        const data = await res.json().catch(() => ({}));
        return { success: false, domain: 'personal', message: data.error || '切换个人域失败', connection: orgConnection };
      } catch (err: any) {
        return { success: false, domain: 'personal', message: err.message || '切换个人域失败', connection: orgConnection };
      }
    }
    // Switch to work: use known org or auto-discover
    let orgId = orgConnection?.orgId || null;
    let orgRole = orgConnection?.orgRole || 'member';
    let orgName = orgConnection?.orgName || '';
    if (!orgId) {
      try {
        const orgsRes = await apiFetch('/api/auth/orgs');
        if (orgsRes.ok) {
          const { orgs } = await orgsRes.json();
          if (orgs && orgs.length > 0) {
            orgId = orgs[0].id;
            orgRole = orgs[0].role || 'member';
            orgName = orgs[0].name || orgs[0].orgName || '';
          }
        }
      } catch (err: any) {
        return { success: false, domain: 'work', message: err.message || '组织列表读取失败', connection: orgConnection };
      }
    }
    if (!orgId) {
      return { success: false, domain: 'work', message: '当前账号还没有可切换的组织', connection: orgConnection };
    }
    try {
      const res = await apiFetch('/api/auth/switch-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, orgRole }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.orgId) {
          const conn = { orgId: data.orgId, orgRole: data.orgRole, orgName, connected: true };
          if (data.token) {
            localStorage.setItem('xf_auth_token', encryptString(data.token));
            socketService.refreshAuth();
          }
          setOrgConnection(conn);
          setWorkDomain('work');
          localStorage.setItem('xf_work_domain', 'work');
          localStorage.setItem('xf_org_connection', JSON.stringify(conn));
          window.dispatchEvent(new CustomEvent('xf:domain-changed', {
            detail: { domain: 'work', orgId: conn.orgId, orgRole: conn.orgRole, orgName: conn.orgName },
          }));
          return { success: true, domain: 'work', message: '已切换到工作域', connection: conn };
        }
      }
      return { success: false, domain: 'work', message: data.error || '切换工作域失败', connection: orgConnection };
    } catch (err: any) {
      return { success: false, domain: 'work', message: err.message || '切换工作域失败', connection: orgConnection };
    }
  };

  const [operationMode, setOperationModeState] = useState<OperationMode>(() => {
    try { return normalizeOperationMode(localStorage.getItem('xf_operation_mode')); } catch { return 'assistant'; }
  });

  const setOperationMode = async (mode: OperationMode) => {
    const normalizedMode = normalizeOperationMode(mode);
    setOperationModeState(normalizedMode);
    localStorage.setItem('xf_operation_mode', normalizedMode);
    try {
      await apiFetch('/api/preferences/operation_mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: normalizedMode }),
        credentials: 'include',
      });
    } catch (e) { console.warn('[AppContext] 捕获到异常:', e); }
  };

  const updateAIConfig = (newConfig: Partial<AIConfig>) => {
    setAiConfig(prev => {
      // Auto-resolve model from per-provider preferences when provider changes
      let resolved = { ...newConfig };
      if (newConfig.provider && !newConfig.model) {
        const savedModels = (() => {
          try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
        })();
        const defaults: Record<string, string> = {
          qwen: 'qwen-plus', deepseek: 'deepseek-chat', openai: 'gpt-4o',
          gemini: 'gemini-2.0-flash', anthropic: 'claude-sonnet-4-6',
        };
        resolved.model = savedModels[newConfig.provider] || defaults[newConfig.provider] || '';
      }
      const updated = { ...prev, ...resolved };
      localStorage.setItem('xf_ai_config', JSON.stringify(updated));

      // Also sync apiKey to server so LLM/STT/TTS providers can read it
      if (updated.apiKey && updated.provider) {
        const KEY_MAP: Record<string, string> = {
          qwen: 'DASHSCOPE_API_KEY',
          deepseek: 'DEEPSEEK_API_KEY',
          openai: 'OPENAI_API_KEY',
          gemini: 'GEMINI_API_KEY',
          anthropic: 'ANTHROPIC_API_KEY',
          ark: 'ARK_API_KEY',
          xiaomi: 'XIAOMI_API_KEY',
          kimi: 'KIMI_API_KEY',
          glm: 'GLM_API_KEY',
          relay: 'RELAY_API_KEY',
        };
        const serverKey = KEY_MAP[updated.provider];
        if (serverKey) {
          saveServerKeys({ [serverKey]: updated.apiKey })
            .catch(err => toast.error(((err) as any).message || 'API 密钥保存失败'));
        }
      }

      // Sync LLM prefs (provider + per-provider models) to server for personality evolution
      if (updated.provider || updated.model) {
        const allModels = (() => {
          try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
        })();
        if (updated.model && updated.provider) {
          allModels[updated.provider] = updated.model;
        }
        apiFetch('/api/preferences/llm', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: updated.provider || prev.provider, models: allModels }),
          credentials: 'include',
        }).catch(() => {});
      }
      return updated;
    });
    toast.success('Neural core configuration synchronized');
  };

  const updateVisionConfig = (newConfig: Partial<VisionConfig>) => {
    setVisionConfig(prev => {
      let resolved = { ...newConfig };
      if (newConfig.provider && !newConfig.model) {
        const savedModels = (() => {
          try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
        })();
        const defaults: Record<string, string> = {
          openai: 'gpt-4o',
          gemini: 'gemini-2.0-flash',
          ark: 'doubao-1-5-vision-pro-32k',
          qwen: 'qwen-vl-max',
          relay: 'qwen2.5-vl-7b-instruct',
        };
        resolved.model = savedModels[newConfig.provider] || defaults[newConfig.provider] || '';
      }

      const updated = { ...prev, ...resolved };
      localStorage.setItem('xf_vision_config', JSON.stringify(updated));

      if (updated.apiKey && updated.provider) {
        const KEY_MAP: Record<string, string> = {
          openai: 'OPENAI_API_KEY',
          gemini: 'GEMINI_API_KEY',
          ark: 'ARK_API_KEY',
          qwen: 'DASHSCOPE_API_KEY',
          relay: 'RELAY_API_KEY',
        };
        const serverKey = KEY_MAP[updated.provider];
        if (serverKey) {
          saveServerKeys({ [serverKey]: updated.apiKey })
            .catch(err => toast.error(((err) as any).message || 'Vision API 密钥保存失败'));
        }
      }

      const allModels = (() => {
        try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
      })();
      if (updated.provider && updated.model) {
        allModels[updated.provider] = updated.model;
        localStorage.setItem('xf_vision_models', JSON.stringify(allModels));
      }
      apiFetch('/api/preferences/vision', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: updated.provider, model: updated.model, models: allModels }),
        credentials: 'include',
      }).catch(() => {});

      return updated;
    });
    toast.success('Vision model configuration synchronized');
  };

  const refreshUser = async () => {
    try {
      const customAuth = await authService.getMe();
      if (customAuth) {
        setUser({ ...customAuth.user, provider: 'custom' } as any);
        // Sync org connection from user data
        const u = customAuth.user as any;
        if (u?.orgId) {
          const conn: OrgConnection = { orgId: u.orgId, orgRole: u.orgRole || 'member', orgName: '', connected: true };
          setOrgConnection(conn);
          localStorage.setItem('xf_org_connection', JSON.stringify(conn));
        }
        try { setAgents(await agentService.listAgents()); } catch (e) { console.warn('[AppContext] 捕获到异常:', e); }
        // Load persisted notifications from server
        try {
          const notifData = await notificationService.fetchNotifications();
          if (notifData.notifications?.length > 0) {
            setNotifications(prev => {
              const existingIds = new Set(prev.map(n => n.id));
              const proactiveGreetingEnabled = localStorage.getItem('xf_allow_proactive_voice') === 'true';
              const newNotifs = notifData.notifications.filter((n: any) =>
                !existingIds.has(n.id) &&
                (proactiveGreetingEnabled || n.type !== 'greeting')
              );
              return [...newNotifs, ...prev].slice(0, 50);
            });
          }
        } catch (e) { console.warn('[AppContext] 捕获到异常:', e); }
        // Load tool overrides from server
        try {
          const toRes = await apiFetch('/api/settings/tool_overrides');
          if (toRes.ok) {
            const serverOverrides = await toRes.json();
            if (serverOverrides && Object.keys(serverOverrides).length > 0) {
              setToolOverrides(serverOverrides);
              localStorage.setItem('xf_tool_overrides', JSON.stringify(serverOverrides));
            }
          }
        } catch (e) { console.warn('[AppContext] 捕获到异常:', e); }
      } else {
        setUser(null);
        setAgents([]);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  useEffect(() => {
    const handleAgentsChanged = async (event: Event) => {
      const agent = (event as CustomEvent).detail?.agent;
      if (agent?.id) {
        setAgents(prev => {
          const exists = prev.some(a => a.id === agent.id);
          return exists ? prev.map(a => a.id === agent.id ? { ...a, ...agent } : a) : [...prev, agent];
        });
        return;
      }
      try { setAgents(await agentService.listAgents()); } catch (e) { console.warn('[AppContext] 捕获到异常:', e); }
    };

    window.addEventListener('xf:agents-changed', handleAgentsChanged);
    return () => window.removeEventListener('xf:agents-changed', handleAgentsChanged);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let settled = false;
    const finish = () => {
      if (!cancelled && !settled) {
        settled = true;
        setLoading(false);
      }
    };
    // 兜底：后端若慢启动/未就绪，最多 15s 后强制结束启动画面，
    // 避免首屏因重试级联长时间卡在"启动中…"（此前需手动刷新才能进）
    const safetyTimer = setTimeout(finish, 15000);

    const init = async () => {
      setLoading(true);
      try {
        const me = await authService.getMe();
        // Re-bootstrap only if not logged in OR session expired
        // Organization creation already updates token via OrgPortal.refreshUser() (line 137) — no need for re-bootstrap check here
        // Bug fix: previously `!(me.user as any)?.orgId` caused personal users (always orgId=null) to be cleared and replaced with admin
        const needsRebootstrap = !me;  // ✅已登录的用户不再强制 re-bootstrap（避免误清个人用户的 token）
        // Bug fix: `result` 之前在 if 块内用 let 声明，块外引用导致 ReferenceError，
        // 被 catch 吞掉后 refreshUser() 永不执行 —— 已登录用户刷新后状态丢失的根因
        let bootstrapSuccess = false;
        if (needsRebootstrap && !cancelled) {
          // Only clear token if actually not logged in
          try { const t = localStorage.getItem('xf_auth_token'); if (!t) { /* no-op */ } } catch (e) { console.warn('[AppContext] Token check failed:', e); }
          // apiFetch 内部已对本地后端做重试等待，这里仅补少量轻量重试，
          // 避免 8 轮 × 内层 10 次重试级联导致首屏阻塞约 100s
          let result = await authService.bootstrap();
          for (let retry = 0; !result.success && !result.autoLoginDisabled && retry < 3 && !cancelled; retry++) {
            const delay = 1000 * (retry + 1); // 1s, 2s, 3s
            console.log(`[Auth] Bootstrap retry ${retry + 1}/3 in ${delay}ms...`);
            await new Promise(r => setTimeout(r, delay));
            result = await authService.bootstrap();
          }
          if (result.success && !cancelled) {
            console.log('[Auth] Auto-logged in via bootstrap as', result.user?.username);
            bootstrapSuccess = true;
          } else if (!cancelled) {
            console.warn('[Auth] Bootstrap failed after retries:', result.error);
            // ✅ 未登录状态下不清空已有 token（避免误杀手动填入的有效 token）
            // No longer clear token here — personal users with valid tokens should be preserved
          }
        }
        // ✅ 只在 bootstrap 成功或有当前用户时才调用 refreshUser()
        if (!cancelled && (bootstrapSuccess || me)) await refreshUser();
      } catch (e) {
        console.warn('[AppContext] 初始化异常（已忽略，界面照常进入）:', e);
      } finally {
        clearTimeout(safetyTimer);
        finish();
      }
    };
    init();
    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, []);

  const login = async () => {
    window.dispatchEvent(new CustomEvent('xf:open-login'));
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setAgents([]);
      // 登出后若在桌面端，复位到桌面首页（DesktopUI 监听 xf:navigate 清空窗口）
      window.dispatchEvent(new CustomEvent('xf:navigate', { detail: { tab: 'home' } }));
      toast.info('已退出登录');
    } catch (error: any) {
      toast.error('退出失败：' + error.message);
    }
  };

  const createAgent = async (name: string, category: string, data: any): Promise<any> => {
    if (!user) {
      toast.error('You must be authenticated to synthesize agents');
      return null;
    }
    try {
      const newAgent = await agentService.createAgent({ name, category, data: JSON.stringify(data) });
      setAgents(prev => [...prev, newAgent]);
      addNotification({ type: 'success', title: 'Agent Synthesized', message: `${name} (${category}) has been created and is ready for use.` });
      toast.success(`${name} has been synthesized`);
      return newAgent;
    } catch (error: any) {
      console.error('Synthesis error:', error);
      toast.error('Synthesis failed: ' + error.message);
    }
  };

  const updateAgent = async (id: string, updates: Partial<Agent>) => {
    try {
      const updated = await agentService.updateAgent(id, updates);
      setAgents(prev => prev.map(a => a.id === id ? { ...a, ...updated } : a));
      toast.success(`Agent "${updated.name || id}" updated`);
      return updated;
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error('Update failed: ' + error.message);
    }
  };

  const deleteAgent = async (id: string) => {
    try {
      await agentService.deleteAgent(id);
      const deleted = agents.find(a => a.id === id);
      setAgents(prev => prev.filter(a => a.id !== id));
      addNotification({ type: 'info', title: 'Agent Released', message: `"${deleted?.name || id}" has been dissolved.` });
      toast.success('Agent essence has been released');
    } catch (error: any) {
      console.error('Deletion error:', error);
      toast.error('Deletion failed: ' + error.message);
    }
  };

  const updateBalance = async (amount: number) => {
    setUser((prev) => prev ? { ...prev, balance: (prev.balance || 0) + amount } : prev);
  };

  const setSelectedVoiceId = (id: string, provider?: string) => {
    setSelectedVoiceIdState(id);
    localStorage.setItem('xf_selected_voice_id', id);
    if (provider) {
      localStorage.setItem('xf_selected_voice_provider', provider);
      // Auto-switch TTS provider to match the selected voice
      apiFetch('/api/voice/provider', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tts: provider }),
      }).catch(() => {});
    }
  };

  const toggleFavoriteVoice = (id: string) => {
    setFavoriteVoices(prev => {
      const next = prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id];
      localStorage.setItem('xf_favorite_voices', JSON.stringify(next));
      return next;
    });
  };

  const setAutoSpeakReply = (v: boolean) => {
    setAutoSpeakReplyState(v);
    localStorage.setItem('xf_auto_speak_reply', String(v));
  };

  const addNotification = (item: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>) => {
    const notification: NotificationItem = {
      ...item,
      id: `notif-${Date.now()}-${crypto.randomUUID()}`,
      timestamp: Date.now(),
      read: false,
    };
    setNotifications(prev => [notification, ...prev].slice(0, 50));
  };

  const markAllNotificationsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const setToolOverride = (name: string, override: ToolOverride) => {
    setToolOverrides(prev => {
      const next = { ...prev, [name]: override };
      localStorage.setItem('xf_tool_overrides', JSON.stringify(next));
      // Sync to server for tool registry awareness
      apiFetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ key: 'tool_overrides', value: next }),
      }).catch(() => {});
      return next;
    });
  };

  return (
    <AppContext.Provider value={{
      user,
      loading,
      agents,
      aiConfig,
      visionConfig,
      selectedVoiceId,
      setSelectedVoiceId,
      favoriteVoices,
      toggleFavoriteVoice,
      autoSpeakReply,
      setAutoSpeakReply,
      notifications,
      unreadCount,
      addNotification,
      markAllNotificationsRead,
      clearNotifications,
      toolOverrides,
      setToolOverride,
      orgConnection,
      workDomain,
      switchDomain,
      operationMode,
      setOperationMode,
      login,
      logout,
      createAgent,
      updateAgent,
      deleteAgent,
      updateBalance,
      refreshUser,
      updateAIConfig,
      updateVisionConfig,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
